// Hideo - "@" mentions with an icon. Ported from the legacy ui/mentions.js with the same output, byte for byte (golden
// tests: tests/mentions.test.ts against tests/fixtures/legacy/mentions-w11.js).
// In long text fields (class .mention-field: video notes, creator bio and notes, analysis, hook content, source notes)
// the user types @ and picks a plan video, a hook, a creator, a source or an analysed video (the menu:
// src/features/mentionMenu.ts; in the script editor: src/features/editor/surface/slash.ts).
// In the saved text a mention is a reference "@[type:id]": the displayed name is read from the data every time,
// so renaming the item updates the mention by itself; deleting it turns the mention into "deleted item".
// On screen a mention is a label (.mention-chip) with the type's icon; on cards it can be clicked to open the item
// (data-open: the click listener of src/features/mentionMenu.ts, installMentions, calls openRef).
import { t, tv } from '../i18n'
import { esc } from '../utils/html'
import type { ProfileData } from '../data/types'
import { useDataStore } from '../stores/data'
import { useDialogsStore } from '../stores/dialogs'
import { useVideoStore } from '../stores/video'
import { useCreatorsStore } from '../stores/creators'
import { app } from './app'
import { SEARCH_ICONS, SEARCH_LABELS } from './search'

/** A reference in stored text: "@[video|hook|creator|source|av:<id>]" (global: use it with replace / matchAll) */
export const MENTION_RE = /@\[(video|hook|creator|source|av):([\w-]+)\]/g
/** A chip's data-ref ("type:id") is a valid reference */
export const validRef = (r: unknown): boolean => /^(video|hook|creator|source|av):[\w-]+$/.test((r || '') as string)

type MentionType = 'video' | 'hook' | 'creator' | 'source' | 'av'
/** Icon and label of each type (labels are English sources, shown with t()) */
export const MENTION_TYPES: Readonly<Record<MentionType, { icon: string; label: string }>> = Object.fromEntries(
  (['video', 'hook', 'creator', 'source', 'av'] as const).map(k => [k, { icon: SEARCH_ICONS[k], label: SEARCH_LABELS[k] }])) as Record<MentionType, { icon: string; label: string }>   // the same as the search's
/** A mentionable item: [type, id, name] */
export type MentionItem = [MentionType, string, string]

/** Current name of the mentioned item (null when it no longer exists); an analysed video also gives its creator.
 *  d: the profile data, when the caller has it (reading it normalises the whole profile: once per text, not per chip) */
export function refLookup(type: string, id: string, d: ProfileData = useDataStore().getData()): { name: string; creatorId?: string } | null {
  if (type === 'video') { const x = d.videos.find(v => v.id === id); return x ? { name: tv(x.title) || t('Untitled') } : null }
  if (type === 'hook') { const x = d.vault.find(v => v.id === id); return x ? { name: x.title } : null }
  if (type === 'creator') { const x = d.creators.find(c => c.id === id); return x ? { name: x.name } : null }
  if (type === 'source') { const x = d.sources.find(s => s.id === id); return x ? { name: x.title } : null }
  if (type === 'av') for (const c of d.creators) { const a = (c.analyzedVideos || []).find(v => v.id === id); if (a) return { name: a.title, creatorId: c.id } }
  return null
}
/** Inside a chip: the type's icon and the item's current name ("deleted item" when it is gone) */
const chipInner = (type: string, r: { name: string } | null): string => `<i class="fas ${MENTION_TYPES[type as MentionType].icon}"></i>${esc(r ? r.name : t('deleted item'))}`
/** The chip of a mention: span.mention-chip with data-ref and the item's current name (clickable: data-open, a click
 *  opens it: openChip) */
export function chipHtml(type: string, id: string, clickable?: boolean, d?: ProfileData): string {
  const r = refLookup(type, id, d)
  return `<span class="mention-chip${r ? '' : ' mention-missing'}" contenteditable="false" data-ref="${type}:${esc(id)}"${clickable && r ? ` data-open title="${t('Open')}"` : ''}>${chipInner(type, r)}</span>`
}
/** Text with references -> HTML to show (text escaped with esc, line breaks kept by the CSS whitespace-pre-line) */
export function mentionHtml(text: unknown, clickable = true): string {
  const s = String(text || '')
  let out = '', last = 0, d: ProfileData | undefined
  for (const m of s.matchAll(MENTION_RE)) { out += esc(s.slice(last, m.index)) + chipHtml(m[1], m[2], clickable, d ||= useDataStore().getData()); last = (m.index as number) + m[0].length }
  return out + esc(s.slice(last))
}
/** Text with references -> plain text with the names (search, previews); d: the profile data, when the caller has it */
export function mentionPlain(text: unknown, d?: ProfileData): string {
  return String(text || '').replace(MENTION_RE, (_m, type: string, id: string) => { const r = refLookup(type, id, d ||= useDataStore().getData()); return '@' + (r ? r.name : t('deleted item')) })
}
/** Refreshes the label names inside already saved HTML (scripts) */
export function refreshChips(root: ParentNode): void {
  let d: ProfileData | undefined
  root.querySelectorAll<HTMLElement>('.mention-chip[data-ref]').forEach(el => {
    if (!validRef(el.dataset.ref)) return
    const [type, id] = (el.dataset.ref as string).split(':'), r = refLookup(type, id, d ||= useDataStore().getData())
    el.innerHTML = chipInner(type, r)
    el.classList.toggle('mention-missing', !r); el.setAttribute('contenteditable', 'false')
  })
}
/** Opens the mentioned item (its page, then its dialog) */
export function openRef(type: string, id: string): void {
  const r = refLookup(type, id)
  if (!r) return
  const dialogs = useDialogsStore(), creators = useCreatorsStore()
  if (type === 'video') { app.switchTab('ped'); useVideoStore().open(id || null, undefined) }
  else if (type === 'hook') { app.switchTab('vault'); dialogs.show('hook', { id: id || null, prefill: undefined }) }
  else if (type === 'creator') { app.switchTab('creators'); creators.openDetail(id) }
  else if (type === 'source') { app.switchTab('sources'); dialogs.show('source', { id: id || null, prefill: undefined }) }
  else if (type === 'av') { app.switchTab('creators'); creators.openDetail(r.creatorId as string); creators.openAnalysed(id) }
}
/** A click on a clickable chip (data-open) opens its item and goes no further (the card or row around it does not get
 *  it), as the chip's inline handler did; true when it was one */
export function openChip(e: Event): boolean {
  const chip = (e.target as Element | null)?.closest?.<HTMLElement>('.mention-chip[data-open][data-ref]')
  if (!chip) return false
  const ref = chip.dataset.ref as string, i = ref.indexOf(':')
  e.stopPropagation(); openRef(ref.slice(0, i), ref.slice(i + 1))
  return true
}
/** Items that can be mentioned, in this order: videos, hooks, creators (each followed by its analysed videos), sources */
export function mentionCandidates(): MentionItem[] {
  const d = useDataStore().getData(), I: MentionItem[] = []
  d.videos.forEach(v => I.push(['video', v.id, tv(v.title) || t('Untitled')]))
  d.vault.forEach(v => I.push(['hook', v.id, v.title]))
  d.creators.forEach(c => { I.push(['creator', c.id, c.name]); (c.analyzedVideos || []).forEach(a => I.push(['av', a.id, a.title])) })
  d.sources.forEach(s => I.push(['source', s.id, s.title]))
  return I
}

// ===== Fields with mentions (contenteditable div with class .mention-field) =====
const mfEl = (el: HTMLElement | string): HTMLElement => typeof el === 'string' ? document.getElementById(el) as HTMLElement : el
/** Stored text -> content of a .mention-field, with the mention chips (not clickable) */
export function mfSet(el: HTMLElement | string, text: unknown): void { mfEl(el).innerHTML = mentionHtml(text, false).replace(/\n/g, '<br>') }
/** Field content -> text with references (Chrome/Safari line breaks arrive as <div>/<br>) */
export function mfGet(el: HTMLElement | string): string {
  let out = ''
  const walk = (n: Node) => n.childNodes.forEach(c => {
    if (c.nodeType === 3) out += c.textContent
    else if (c.nodeType === 1) {
      const e = c as HTMLElement
      if (e.dataset && validRef(e.dataset.ref)) out += `@[${e.dataset.ref}]`
      else if (e.tagName === 'BR') out += '\n'
      else { if (/^(DIV|P)$/.test(e.tagName) && out && !out.endsWith('\n')) out += '\n'; walk(e) }
    }
  })
  walk(mfEl(el))
  return out.replace(/\u00a0/g, ' ').replace(/\n+$/, '')
}
